import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { createUserPermission, fetchUserPermissionsByUserId, getUserById, updateUserPermission } from '../../../core/data/redux/user/userAction';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../../../core/data/redux/store';
import { SidebarDataTest } from '../../../core/data/json/sidebarMenu';
import { message } from 'antd';
import { all_routes } from '../../router/all_routes';

// Extract labels from sidebar data
const labels = SidebarDataTest.flatMap(menu =>
    menu.submenuItems.flatMap(item => {
        const subLabels = item.submenuItems?.map(sub => sub.label) || [];
        return [item.label, ...subLabels];
    })
);

// Build initial module/permission state
const initialSelectAllModules = Object.fromEntries(
    labels.map(label => [label, false])
);

const parentToChildrenMap = Object.fromEntries(
    SidebarDataTest.flatMap(menu =>
        menu.submenuItems.map(item => {
            const subLabels = item.submenuItems?.map(sub => sub.label) || [];
            return [item.label, subLabels];
        })
    )
);

const initialPermissions = Object.fromEntries(
    labels.map(label => [
        label,
        {
            AllowAll: false,
            Add: false,
            Edit: false,
            Delete: false,
            View: false,
            Import: false,
            Export: false,
        },
    ])
);

const UserPermissionPage = () => {
    const dispatch = useDispatch<AppDispatch>();
    const { userId } = useParams<{ userId: string }>();
    const parsedUserId = userId ? Number(userId) : NaN;
    const [expandedParents, setExpandedParents] = useState<Record<string, boolean>>({});
    const [permissions, setPermissions] = useState<any>(initialPermissions);
    const [selectAllModules, setSelectAllModules] = useState<any>(initialSelectAllModules);
    const { userPermissions } = useSelector((state: any) => state.user);
    const { users } = useSelector((state: any) => state.user);

    useEffect(() => {
        if (parsedUserId) {
            dispatch(fetchUserPermissionsByUserId(parsedUserId));
            dispatch(getUserById(parsedUserId))
        }
    }, [dispatch, parsedUserId]);

    useEffect(() => {
        if (userPermissions.length > 0) {
            const updatedPermissions = { ...permissions };
            userPermissions.forEach((permission: any) => {
                const { PermissionKey, PermissionValues } = permission;
                if (updatedPermissions[PermissionKey]) {
                    updatedPermissions[PermissionKey].AllowAll = PermissionValues.includes('AllowAll');
                    PermissionValues.forEach((perm: string) => {
                        if (updatedPermissions[PermissionKey][perm] !== undefined) {
                            updatedPermissions[PermissionKey][perm] = true;
                        }
                    });
                }
            });
            setPermissions(updatedPermissions);
        }
    }, [userPermissions]);

    const toggleExpand = (label: string) => {
        setExpandedParents(prev => ({
            ...prev,
            [label]: !prev[label],
        }));
    };

    const handleModuleSelectChange = (module: string) => {
        setSelectAllModules((prev: any) => {
            const newValue = !prev[module];
            const updated = { ...prev, [module]: newValue };

            const children = parentToChildrenMap[module];
            if (children?.length > 0) {
                children.forEach(child => {
                    updated[child] = newValue;
                });
            }
            return updated;
        });

        setPermissions((prevPermissions: any) => {
            const updated = { ...prevPermissions };
            const emptyPermissions = {
                AllowAll: false,
                Add: false,
                Edit: false,
                Delete: false,
                View: false,
                Import: false,
                Export: false,
            };

            const children = parentToChildrenMap[module];
            if (children?.length > 0) {
                children.forEach(child => {
                    updated[child] = { ...emptyPermissions };
                });
            }
            return updated;
        });
    };

    const handleCheckboxChange = (module: string, permission: string) => {
        setPermissions((prev: any) => {
            const updated = {
                ...prev,
                [module]: {
                    ...prev[module],
                    [permission]: !prev[module][permission]
                }
            };
            const allChecked = ['Add', 'Edit', 'Delete', 'View', 'Import', 'Export']
                .every(p => updated[module][p]);
            updated[module].AllowAll = allChecked;
            return updated;
        });
    };

    const handleAllowAllChange = (module: string) => {
        setPermissions((prev: any) => {
            const checked = !prev[module].AllowAll;
            return {
                ...prev,
                [module]: {
                    AllowAll: checked,
                    Add: checked,
                    Edit: checked,
                    Delete: checked,
                    View: checked,
                    Import: checked,
                    Export: checked
                }
            };
        });
    };

    const handleSubmit = async () => {
        try {
            const permissionValues = Object.keys(permissions).map(module => {
                const perms = permissions[module];
                return {
                    module,
                    permissions: Object.keys(perms)
                        .filter(key => perms[key])
                };
            }).filter(p => p.permissions.length > 0);

            for (const permission of permissionValues) {
                const permissionData = {
                    UserId: parsedUserId,
                    PermissionKey: permission.module,
                    PermissionValues: permission.permissions
                };

                const existing = userPermissions.find(
                    (perm: any) => perm.UserId === parsedUserId && perm.PermissionKey === permission.module
                );

                if (existing) {
                    await dispatch(updateUserPermission({
                        userPermissionId: existing.UserPermissionId,
                        permissionData
                    }));
                } else {
                    await dispatch(createUserPermission(permissionData));
                }
            }
            message.success('Permissions saved successfully');
            if (parsedUserId) {
                dispatch(fetchUserPermissionsByUserId(parsedUserId));
            }
        } catch (error) {
            message.error('Failed to save permissions');
        }
    };

    return (
        <div className="page-wrapper">
            <div className="content">
                <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
                    <div className="my-auto mb-2">
                        <h2 className="mb-1">User Permissions</h2>
                        <nav>
                            <ol className="breadcrumb mb-0">
                                <li className="breadcrumb-item">
                                    <Link to={all_routes.adminDashboard}>
                                        <i className="ti ti-smart-home" />
                                    </Link>
                                </li>
                                <li className="breadcrumb-item"><Link to="#">Dashboard</Link></li>
                                <li className="breadcrumb-item">Administration</li>
                                <li className="breadcrumb-item active">User Permissions</li>
                            </ol>
                        </nav>
                    </div>
                    <button onClick={handleSubmit} className="btn btn-primary mb-2">Save Permissions</button>
                </div>

                <div className="card">
                    <div className="card-header d-flex align-items-center justify-content-between flex-wrap">
                        <h5>
                            <span className="badge bg-success">{users[0].data.DisplayName} </span> - Permissions
                        </h5>
                        <p>User ID: <span className="text-gray-9 fw-medium">{parsedUserId}</span></p>
                    </div>
                    <div className="card-body p-0">
                        <div className="table-responsive">
                            <table className="table">
                                <thead className="thead-light">
                                    <tr>
                                        <th>Modules</th>
                                        <th>Allow All</th>
                                        <th>Add</th>
                                        <th>Edit</th>
                                        <th>Delete</th>
                                        <th>View</th>
                                        <th>Import</th>
                                        <th>Export</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {SidebarDataTest.flatMap(menu =>
                                        menu.submenuItems.map(parent => (
                                            <React.Fragment key={parent.label}>
                                                <tr className="bg-light">
                                                    <td className="fw-bold text-gray-9">
                                                        <button
                                                            className="btn btn-sm p-0 me-2"
                                                            onClick={() => toggleExpand(parent.label)}
                                                        >
                                                            {expandedParents[parent.label] ? '▼' : '▶'}
                                                        </button>
                                                        <input
                                                            type="checkbox"
                                                            checked={selectAllModules[parent.label]}
                                                            onChange={() => handleModuleSelectChange(parent.label)}
                                                        />{" "}
                                                        {parent.label}
                                                    </td>
                                                    <td className="form-check form-check-md">
                                                        <input
                                                            type="checkbox"
                                                            className="form-check-input fs-4 border-2 bg-success"
                                                            checked={permissions[parent.label].AllowAll}
                                                            onChange={() => handleAllowAllChange(parent.label)}
                                                            disabled={!selectAllModules[parent.label]}
                                                        />
                                                    </td>
                                                    {['Add', 'Edit', 'Delete', 'View', 'Import', 'Export'].map(p => (
                                                        <td key={p}>
                                                            <div className="form-check form-check-md">
                                                                <input
                                                                    type="checkbox"
                                                                    className="form-check-input fs-4 border-2 bg-secondary"
                                                                    checked={permissions[parent.label][p]}
                                                                    onChange={() => handleCheckboxChange(parent.label, p)}
                                                                    disabled={!selectAllModules[parent.label]}
                                                                />
                                                            </div>
                                                        </td>
                                                    ))}
                                                </tr>
                                                {expandedParents[parent.label] && parent.submenuItems?.map(child => (
                                                    <tr key={child.label}>
                                                        <td className="ps-5 text-gray-9">
                                                            <input
                                                                type="checkbox"
                                                                checked={selectAllModules[child.label]}
                                                                onChange={() => handleModuleSelectChange(child.label)}
                                                            />{" "}
                                                            {child.label}
                                                        </td>
                                                        <td className="form-check form-check-md">
                                                            <input
                                                                type="checkbox"
                                                                className="form-check-input fs-4 border-2 bg-success"
                                                                checked={permissions[child.label].AllowAll}
                                                                onChange={() => handleAllowAllChange(child.label)}
                                                                disabled={!selectAllModules[child.label]}
                                                            />
                                                        </td>
                                                        {['Add', 'Edit', 'Delete', 'View', 'Import', 'Export'].map(p => (
                                                            <td key={p}>
                                                                <div className="form-check form-check-md">
                                                                    <input
                                                                        type="checkbox"
                                                                        className="form-check-input fs-4 border-2 bg-secondary"
                                                                        checked={permissions[child.label][p]}
                                                                        onChange={() => handleCheckboxChange(child.label, p)}
                                                                        disabled={!selectAllModules[child.label]}
                                                                    />
                                                                </div>
                                                            </td>
                                                        ))}
                                                    </tr>
                                                ))}
                                            </React.Fragment>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
                <button onClick={handleSubmit} className="btn btn-primary mt-3">Save Permissions</button>
                <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3 mt-4">
                    <p className="mb-0">2024 - 2025© hrmtree.</p>
                    <p>Developed By{" "}<Link to="#" className="text-primary">Leadchain Systems</Link></p>
                </div>
            </div>
        </div>
    );
};

export default UserPermissionPage;
